/**
 * serviceRunService — runs, recurrence rules and assignments.
 * apiClient auto-transforms camelCase↔snake_case.
 */

import { apiClient } from '@/shared/services/api';
import type {
  ServiceRun, ServiceRunListItem, ServiceRecurrenceRule, RecurrenceRuleFormData,
  RunAssignment, RunAssignmentFormData, RunEditFormData, RunEditMode,
} from '../types';

export async function getServiceRuns(dateFrom: string, dateTo: string): Promise<ServiceRunListItem[]> {
  const res = await apiClient.get<{ results: ServiceRunListItem[]; count: number }>(
    '/api/v1/services/runs/', { params: { date_from: dateFrom, date_to: dateTo, page_size: 500 } },
  );
  return res.data.results;
}

/**
 * PATCH a run's per-instance fields. When the run belongs to a generated
 * series, `editMode` propagates time/capacity/transport/notes across scope
 * (single | this_and_future | all) per the backend contract; date and
 * assignments stay per-run.
 */
export async function updateServiceRun(
  id: string,
  data: RunEditFormData,
  options?: { editMode?: RunEditMode },
): Promise<ServiceRun> {
  const payload: Record<string, unknown> = { ...data };
  if (options?.editMode && options.editMode !== 'single') payload.editMode = options.editMode;
  const res = await apiClient.patch<ServiceRun>(`/api/v1/services/runs/${id}/`, payload);
  return res.data;
}

/** Upcoming runs of one product — the "another run" / add-to-run picker source. */
export async function getServiceProductRuns(productId: string, dateFrom: string, dateTo: string): Promise<ServiceRunListItem[]> {
  const res = await apiClient.get<{ results: ServiceRunListItem[]; count: number }>(
    '/api/v1/services/runs/', { params: { product: productId, date_from: dateFrom, date_to: dateTo, status: 'scheduled', page_size: 200 } },
  );
  return res.data.results;
}

/** Copy this run's crew onto its series (replace semantics). */
export async function propagateRunAssignments(
  id: string,
  editMode: 'this_and_future' | 'all',
): Promise<{ updated: number; created: number; deleted: number; completedSkipped: number }> {
  const res = await apiClient.post<{ updated: number; created: number; deleted: number; completedSkipped: number }>(
    `/api/v1/services/runs/${id}/propagate-assignments/`,
    { editMode },
  );
  return res.data;
}

export async function createRecurrenceRule(data: RecurrenceRuleFormData, locationId: string): Promise<ServiceRecurrenceRule> {
  const res = await apiClient.post<ServiceRecurrenceRule>('/api/v1/services/recurrence-rules/', { ...data, location: locationId });
  return res.data;
}

export async function generateRuns(ruleId: string, body?: { fromDate?: string; toDate?: string }): Promise<{ generated: number }> {
  const res = await apiClient.post<{ generated: number }>(`/api/v1/services/recurrence-rules/${ruleId}/generate/`, body ?? {});
  return res.data;
}

export async function createAssignment(data: RunAssignmentFormData): Promise<RunAssignment> {
  const res = await apiClient.post<RunAssignment>('/api/v1/services/assignments/', data);
  return res.data;
}

export async function deleteAssignment(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/services/assignments/${id}/`);
}
